import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays, parse } from "date-fns";
import { de } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Snowflake,
  Calendar as CalendarIcon,
  Users,
  Ticket,
  Gift,
  Download,
  RefreshCw,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Phone,
  CheckCircle2,
  AlertCircle,
  Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDeDate = (d) => format(d, "dd.MM.yyyy");

const TagesplanPage = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [authHeader, setAuthHeader] = useState(null);

  // Auth check
  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    setAuthHeader({ Authorization: `Basic ${auth}` });
  }, [navigate]);

  const dateStr = formatDeDate(date);

  const fetchData = useCallback(async () => {
    if (!authHeader) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/tagesplan/${dateStr}`, { headers: authHeader });
      setData(resp.data);
    } catch (err) {
      console.error("Tagesplan fetch failed:", err);
      if (err.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [dateStr, authHeader, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const handlePdfDownload = async () => {
    if (!authHeader) return;
    try {
      const resp = await axios.get(`${API}/admin/tagesplan/${dateStr}/pdf`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Tagesplan-${dateStr.replace(/\./g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };

  const handleSendEmailNow = async () => {
    if (!authHeader) return;
    try {
      await axios.post(`${API}/admin/tagesplan/email-now`, {}, { headers: authHeader });
      // simple alert; could replace with toast
      window.alert("Tagesplan-E-Mail wurde an info@groemitzereiszeit.de versendet.");
    } catch (err) {
      console.error("Email send failed:", err);
      window.alert("Fehler beim Versand der E-Mail.");
    }
  };

  // Helpers for "next 30 min" highlight
  const isUpcomingNext30Min = (timeSlot) => {
    if (!timeSlot) return false;
    const start = timeSlot.split("-")[0]?.trim();
    if (!start) return false;
    try {
      const dt = parse(`${dateStr} ${start}`, "dd.MM.yyyy HH:mm", new Date());
      const diffMin = (dt.getTime() - now.getTime()) / 60000;
      return diffMin >= 0 && diffMin <= 30;
    } catch {
      return false;
    }
  };

  const isToday = formatDeDate(new Date()) === dateStr;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/dashboard")}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <Snowflake className="w-7 h-7 text-sky-500" />
            <div>
              <h1
                className="text-lg md:text-xl font-semibold text-slate-900"
                style={{ fontFamily: "Playfair Display" }}
              >
                Tagesplan
              </h1>
              <p className="text-xs md:text-sm text-slate-500">
                {format(date, "EEEE, d. MMMM yyyy", { locale: de })}
                {isToday && <span className="ml-2 text-sky-600 font-medium">· heute</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDate(addDays(date, -1))}
              data-testid="prev-day-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDate(new Date())}
              data-testid="today-btn"
            >
              Heute
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDate(addDays(date, 1))}
              data-testid="next-day-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              size="sm"
              onClick={handlePdfDownload}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendEmailNow}
              data-testid="send-email-now-btn"
              title="Tagesplan jetzt per E-Mail an info@groemitzereiszeit.de senden"
            >
              <Mail className="w-4 h-4 mr-1" />
              E-Mail
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        {/* Stats grid - tablet-optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tagesplan-stats">
          <StatCard
            icon={<Ticket className="w-7 h-7 text-amber-600" />}
            iconBg="bg-amber-100"
            label="Silvester"
            value={data?.silvester?.total_persons ?? 0}
            sub={
              data?.silvester?.applies
                ? `${data.silvester.checked_in_persons} eingelassen · ${data.silvester.open_persons} offen`
                : "Kein Silvester-Termin"
            }
            testId="stat-silvester"
          />
          <StatCard
            icon={<Users className="w-7 h-7 text-sky-600" />}
            iconBg="bg-sky-100"
            label="Eisstock-Sessions"
            value={data?.eisstock?.total_groups ?? 0}
            sub={`${data?.eisstock?.checked_in_groups ?? 0} eingecheckt · ${data?.eisstock?.open_groups ?? 0} offen`}
            testId="stat-eisstock"
          />
          <StatCard
            icon={<Users className="w-7 h-7 text-emerald-600" />}
            iconBg="bg-emerald-100"
            label="Personen Eisstock"
            value={data?.eisstock?.total_persons ?? 0}
            sub="Gesamt eingebuchte Personen"
            testId="stat-personen"
          />
          <StatCard
            icon={<Gift className="w-7 h-7 text-rose-600" />}
            iconBg="bg-rose-100"
            label="Gutscheine eingelöst"
            value={data?.gutscheine_redeemed?.count ?? 0}
            sub={`heute (${dateStr})`}
            testId="stat-gutscheine"
          />
        </div>

        {/* Eisstock list */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-sky-600" />
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Playfair Display" }}>
                  Eisstock-Sessions des Tages
                </h2>
              </div>
              {isToday && data?.eisstock?.bookings?.some((b) => isUpcomingNext30Min(b.time_slot) && !b.checked_in) && (
                <Badge className="bg-amber-100 text-amber-800 gap-1" data-testid="next-30-banner">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Anstehende Check-ins in den nächsten 30 Min
                </Badge>
              )}
            </div>

            {!data || data.eisstock.bookings.length === 0 ? (
              <div className="px-6 py-12 text-center text-slate-500">
                Keine Buchungen für diesen Tag.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.eisstock.bookings.map((b) => {
                  const upcoming = isToday && isUpcomingNext30Min(b.time_slot) && !b.checked_in;
                  return (
                    <div
                      key={b.id}
                      className={`px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 ${
                        upcoming ? "bg-amber-50" : "hover:bg-slate-50"
                      }`}
                      data-testid={`eisstock-row-${b.id}`}
                    >
                      <div className="flex items-center gap-3 md:w-44">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold flex-shrink-0 ${upcoming ? "bg-amber-200 text-amber-900" : "bg-sky-100 text-sky-700"}`}>
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-base font-semibold text-slate-900">{b.time_slot}</div>
                          {upcoming && <div className="text-xs text-amber-700 font-medium">in &lt; 30 Min</div>}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-base">{b.name}</div>
                        <div className="text-sm text-slate-500 flex flex-wrap gap-3 mt-0.5">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            <a href={`tel:${b.phone}`} className="hover:text-sky-600">{b.phone}</a>
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" /> {b.group_size} Personen
                          </span>
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        {b.checked_in ? (
                          <Badge className="bg-green-100 text-green-800 gap-1 text-sm py-1.5 px-3">
                            <CheckCircle2 className="w-4 h-4" />
                            Eingecheckt
                          </Badge>
                        ) : (
                          <Badge className="bg-sky-100 text-sky-800 text-sm py-1.5 px-3">Offen</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Silvester list (only when applies) */}
        {data?.silvester?.applies && data.silvester.bookings.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                <Ticket className="w-5 h-5 text-amber-600" />
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Playfair Display" }}>
                  Silvester-Tickets
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {data.silvester.bookings.map((b) => (
                  <div
                    key={b.id}
                    className="px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 hover:bg-slate-50"
                    data-testid={`silvester-row-${b.id}`}
                  >
                    <div className="md:w-44">
                      <Badge className={b.ticket_type === "vip" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-800"}>
                        {b.ticket_type === "vip" ? "VIP" : "Normal"} · {b.ticket_count}x
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 text-base">{b.name}</div>
                      <div className="text-sm text-slate-500">{b.email}</div>
                    </div>
                    <div className="flex-shrink-0">
                      {b.checked_in ? (
                        <Badge className="bg-green-100 text-green-800 gap-1 text-sm py-1.5 px-3">
                          <CheckCircle2 className="w-4 h-4" />
                          Eingecheckt
                        </Badge>
                      ) : (
                        <Badge className="bg-sky-100 text-sky-800 text-sm py-1.5 px-3">Offen</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer info */}
        <p className="text-xs text-slate-400 text-center pt-2">
          Aktualisiert sich automatisch alle 30 Sekunden · Stand: {format(now, "HH:mm:ss")}
          <br />
          📧 Tägliche Tagesplan-E-Mail wird automatisch um 07:00 Uhr an info@groemitzereiszeit.de versendet.
        </p>
      </main>
    </div>
  );
};

const StatCard = ({ icon, iconBg, label, value, sub, testId }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-4 md:p-5">
      <div className="flex items-center gap-3 md:gap-4">
        <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs md:text-sm text-slate-500">{label}</p>
          <p className="text-2xl md:text-3xl font-bold text-slate-900" data-testid={testId}>
            {value}
          </p>
          <p className="text-xs text-slate-500 truncate">{sub}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default TagesplanPage;
